import axios, { AxiosResponse, AxiosRequestConfig, AxiosError } from "axios";
import { User } from "@/core/models/global/User";
import { RegisterResponse } from "@/core/models/auth/RegisterResponse";
import { LoginResponse } from "@/core/models/auth/LoginResponse";
import { AuthStatus } from "@/core/models/auth/AuthStatus";
import { z } from "zod";
import { EMAIL_NOT_VERIFIED_EVENT, emailNotVerifiedMessage } from "@/utils/emailNotVerified";
import type { InterviewStatus, AdmissionsInterviewStatus } from "@/types/interviews";
import type { IHousingDetailsData } from "@/components/housingPage/HousingDetailsForm";
import type { IPreviousAddressFormData } from "@/components/housingPage/PreviousAddressForm";
import type { PaymentReceipt } from "@/components/PaymentConfirmationModal";
import { TestUser } from "@/core/models/auth/TestUser";
import { logError } from "./logger";
import { RegisterRequest } from "@/core/models/auth/RegisterRequest"; 
import { EnrollmentDocumentsInfoDto, DocFile, isDocumentJob, type DocumentJobInfo } from "../core/models/global/DocFile";
import { DocumentUploadStatus, MilitaryDocumentsStatus, MilitaryDocumentType, TechCheckStatus } from "../core/models/global/DocumentUploadStatus";
import { EmploymentInfoFormData } from "../core/models/profile/EmploymentInfoFormData";
import { FileUploadData, IFileUploadData } from "../components/global/FileUpload";
import { clearApplicantStorage } from "@/utils/clearApplicantStorage";
// Shared axios client
// axios.interceptors.response.use(
//     response => response,
//     error => {
//         if (error.response && error.response.status === 401) {
//             // Fabricate a successful empty response so downstream code treats it as OK
//             const fake: AxiosResponse = {
//                 data: {},
//                 status: 200,
//                 statusText: 'OK',
//                 headers: error.response.headers || {},
//                 config: error.config,
//                 request: error.request
//             };
//             return Promise.resolve(fake);
//         }
//         return Promise.reject(error);
//     }
// );

const apiClient = axios.create({
    withCredentials: true,
    timeout: 30000,
});
// SECURITY: a 401 means the session is gone (expired / signed out elsewhere) — drop any applicant
// data persisted in Web Storage. No navigation here (routing reacts to auth state), so no loops.
apiClient.interceptors.response.use(
    response => response,
    error => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            clearApplicantStorage();
        }
        // A 403 for an unverified e-mail: tell the app, which says why and opens the verify screen.
        const notVerified = axios.isAxiosError(error) && error.response?.status === 403
            ? emailNotVerifiedMessage(error.response.data)
            : null;
        if (notVerified) {
            window.dispatchEvent(new CustomEvent(EMAIL_NOT_VERIFIED_EVENT, { detail: notVerified }));
        }
        return Promise.reject(error);
    }
);
// Add interceptor to the dedicated client as well
// apiClient.interceptors.response.use(
//     response => response,
//     error => {
//         if (error.response && error.response.status === 401) {
//             const fake: AxiosResponse = {
//                 data: {},
//                 status: 200,
//                 statusText: 'OK',
//                 headers: error.response.headers || {},
//                 config: error.config,
//                 request: error.request
//             };
//             return Promise.resolve(fake);
//         }
//         return Promise.reject(error);
//     }
// );

import { BASE_PATH, absoluteUrl } from "@/utils/basePath";

// ---------- helpers ----------
function stripTrailingSlash(s: string) {
    return s.replace(/\/+$/, "");
}

export function getApiBaseUrl(): string {
    // An absolute override, for running the client against a backend on a different origin.
    const explicit = import.meta.env.VITE_API_TARGET?.trim();
    if (explicit) return stripTrailingSlash(explicit);

    // Otherwise same-origin, under wherever this build is mounted.
    //
    // This replaced ~45 lines that sniffed window.location.pathname for "/SEPDev" (plus a lowercase
    // "/sepdev" variant, plus a hardcoded blocklist of route names, plus a hardcoded
    // "http://localhost:5045/SEPDev" for one specific port). All of it was trying to answer a question
    // the build already knows the answer to. Sniffing the URL is also wrong in principle: it reports
    // where the USER currently is, not where the APP is mounted, and those are indistinguishable in
    // general -- which is exactly why the blocklist existed and why it still failed at site root.
    return BASE_PATH;
}

// ---------- error helper ----------
export function handleError(
    e: unknown,
    consoleMessage: string,
    extraCode?: () => string
): string {
    if (e instanceof Error) {
        logError(consoleMessage + " " + e.message);
        if (extraCode) extraCode();
        return e.message;
    }
    const msg = "An unknown error occurred.";
    logError(consoleMessage + " " + msg);
    return msg;
}

// ---------- models ----------
export class ProfilePageData {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    dateOfBirth?: string;
    gender?: number;
    citizenshipStatus?: number;
    streetAddress?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    phoneNumber?: string;
    emailAddress?: string;
    emergencyContactFirstName?: string;
    emergencyContactLastName?: string;
    emergencyContactPhone?: string;
    emergencyContactRelationship?: string;
    highSchoolName?: string;
    highSchoolGraduationYear?: string;
    collegeName?: string;
    collegeGraduationYear?: string;
    employerName?: string;
    jobTitle?: string;
    employmentStartDate?: string;
    employmentEndDate?: string;
    militaryBranch?: string;
    militaryServiceStartDate?: string;
    militaryServiceEndDate?: string;
    ferpaAuthorization?: boolean; // fixed typo
    additionalInfo?: string;
    hasSpecialNeeds?: boolean;
    specialNeedsExplanation?: string;
    hasTransportation?: boolean;
    isCurrentlyResidingWithFamily?: boolean;
    hasFriendAttendingWantingToShareApt?: boolean;
    friendsAttendingSharingApt?: string;
    isResidentOfAccomackOrNorthamptonCounties?: boolean;
    socialSecurityNumber?: string;
    driverLicenseNumber?: string;
    passportNumber?: string;
    visaStatus?: string;
    maritalStatus?: string;
    dependentsCount?: string;
    previousEmployerName?: string;
    previousJobTitle?: string;
    previousEmploymentStartDate?: string;
    previousEmploymentEndDate?: string;
}

export interface UserResponse {
    applicantId?: number;
    id?: string;
    name?: string;
    email?: string;
    role?: string;
    emailVerified?: boolean;
    isEmailVerified?: boolean;
    requiresVerification?: boolean;
    firstVisit?: boolean;
    campusLocationId?: number;
    programOfInterestId?: number;
    ProfilePageData?: ProfilePageData;
    firstName?: string;
    lastName?: string;
    studentNumber?: string;
    studentId?: string;
    name_?: string;
    // New: CompletedPages returned from server (array of objects with pageId GUIDs)
    completedPages?: Array<{ applicantId?: number; pageId?: string }>;
}
export interface CampusLocation {
    id: string;
    name: string;
    city: string;
    state: string;
    address: string;
    phone: string;
    region: string;
}
export interface Program {
    id: string;
    name: string;
    description: string;
    duration: string;
    type: string;
    category: string;
}
export interface LoginRequest {
    identifier: string;
    password: string;
}

export interface CheckAccountResponse {
    exists: boolean;
    validNexusAccount?: boolean;
    accountStatusId?: number;
    accountType: string;
    /** E-mail check: a Reach lead for this address was created recently, so the details form can be skipped. */
    recentLeadFound?: boolean;
    message?: string;
    firstName?: string;
    lastName?: string;
    campusName?: string;
}
export interface VerifyEmailResponse {
    success: boolean;
    Message?: string;
}
export interface SecureDataRequest {
    jsonData: string;
    pageId?: string;
    /** When true the server runs a full completion check after saving. Use on Next/Submit, leave false for auto-saves. */
    checkCompletion?: boolean;
}

export interface FormValidationResult {
    saved: boolean;
    isComplete: boolean;
    fieldErrors: Record<string, string[]>;
    incompleteReason?: string;
}

export interface StoreSecureDataResponse {
    success: boolean;
    saved: boolean;
    result: FormValidationResult;
}
interface ApiEnvelope<T> {
    success: boolean;
    data: T;
    message: string;
}

export interface ICmDocType {
    CmDocTypeId: number;
    CmDocTypeCode: string;
    CmDocType: string;
    ImageNowDocDesc?: string;
}

export interface ICmDocument {
    CmDocumentId: number;
    SyStudentId: number;
    CmDocTypeId: number;
    DateReq?: Date | string;
    DateRecv?: Date | string;
    CmDocStatusId: number;
    CmDocStatus: string;
    ImageNowDocId0?: string;
    ImageNowDocId1?: string;
    CmDocType?: ICmDocType;
}

export interface IStudentDocumentsResponse {
    documents: ICmDocument[];
}

export interface ITranscriptStatusResponse {
    hasTranscript: boolean;
    document?: {
        CmDocumentId: number;
        DateRecv: Date | string;
        CmDocStatus: string;
    };
    /** Date the secondary-education transcript was requested (ApplicantSecondaryEducation.DateReq) */
    dateReq?: Date | string | null;
    /** Date the secondary-education transcript was received (ApplicantSecondaryEducation.DateRecd) */
    dateRecd?: Date | string | null;
    /** True when ECPI was unable to request the transcript (ApplicantSecondaryEducation.CannotReqTranscript) */
    cannotReqTranscript?: boolean | null;
    /** True when there was an issue verifying the transcript (ApplicantSecondaryEducation.VerificationIssue) */
    verificationIssue?: boolean | null;
    /** Name of the student's high school (ApplicantSecondaryEducation detail) */
    highSchoolName?: string | null;
}

export interface IEducationTypeResponse {
    educationType: string | null;
}

export interface ITranscriptDueDateResponse {
    daysUntilDue: number;
    dueMessage: string;
    dueDate?: string;
    adProgramId?: number;
}

export interface ICampusAddress {
    syCampusId: number;
    campusName: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    phone?: string;
}

export interface ICampusAddressResponse {
    hasCampus: boolean;
    campus?: ICampusAddress;
}

export interface IPersonalProfileCompletionStatus {
    isComplete: boolean;
    applicantId: number;
}

// ---------- core request ----------
/**
 * Calls that create or finish an account write to the database and can be slow when it is busy. The server completes them
 * anyway, so a client that gives up early (the default is 30 s) only leaves the user looking at an error for an account that
 * then exists. 0 means no client timeout: the person waits for the answer (the form says it can take a minute) and the hosting
 * front end ends a request that is still unanswered after about 230 s, the one limit left.
 */
const ACCOUNT_CREATION_TIMEOUT_MS = 0;

// Identical GETs started while the first is still running share its response (UAT SEP-063: the dashboard and its sidebar
// both loaded every tile status, so each call ran twice). The entry is dropped the moment the request settles, so a read
// started afterwards always goes to the server; nothing is cached.
const inFlightGets = new Map<string, Promise<unknown>>();

export const apiRequest = <T>(
    endpoint: string,
    options: AxiosRequestConfig = {}
): Promise<T> => {
    const method = (options.method ?? "GET").toString().toUpperCase();
    const shareable = method === "GET" && !options.signal && !options.responseType && !options.data && !options.headers;
    if (!shareable) return performApiRequest<T>(endpoint, options);

    const key = `${endpoint}|${JSON.stringify(options.params ?? null)}`;
    const running = inFlightGets.get(key);
    if (running) return running as Promise<T>;

    const request = performApiRequest<T>(endpoint, options).finally(() => {
        if (inFlightGets.get(key) === request) inFlightGets.delete(key);
    });
    inFlightGets.set(key, request);
    return request;
};

const performApiRequest = async <T>(
    endpoint: string,
    options: AxiosRequestConfig = {}
): Promise<T> => {
    const apiBase = getApiBaseUrl();
    const fullUrl = endpoint.startsWith("/api")
        ? apiBase + endpoint
        : apiBase +
          "/api" +
          (endpoint.startsWith("/") ? endpoint : "/" + endpoint);
    const config: AxiosRequestConfig = {
        url: fullUrl,
        method: options.method ?? "GET",
        withCredentials: true,
        timeout: 30000,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers ?? {}),
        },
        ...options,
    };
    try {
        const response = await apiClient.request<T>(config);
        return response.data as T;
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            if (status === 401) {
                // Return null instead of {} so callers can distinguish "unauthenticated" from a real empty response
                return null as unknown as T;
            }
            if (status && status >= 500) {
                logError("Server error:", status);
            }
        }
        throw error;
    }
};

export async function apiRequestUnwrapped<T>(
    endpoint: string,
    options: AxiosRequestConfig = {}
): Promise<T> {
    const body = await apiRequest<unknown>(endpoint, options);
    if (body && typeof body === "object") {
        const obj = body as Record<string, unknown>;
        if ("Data" in obj) return obj["Data"] as T;
        if ("data" in obj) return obj["data"] as T;
    }
    return body as T;
}
// ---------- background document jobs ----------
const DOCUMENT_JOB_POLL_MS = 2000;
// Preparing a document takes about a minute; past this the job keeps running server-side and the applicant can retry later.
const DOCUMENT_JOB_WAIT_MS = 4 * 60 * 1000;

/** A background document job that will not produce the document now (failed, or PandaDoc is down and it is retrying). */
export class DocumentJobError extends Error {
    constructor(readonly job: DocumentJobInfo) {
        super(job.message || "We could not prepare the document. Please try again.");
    }
}

/**
 * Polls a document job (202 from an endpoint that prepares a document) until it succeeds, and returns it. Throws
 * DocumentJobError when it fails or reports PandaDoc unavailable, and an Error when it takes too long.
 */
export async function waitForDocumentJob(first: DocumentJobInfo): Promise<DocumentJobInfo> {
    let job: DocumentJobInfo | null = first;
    const deadline = Date.now() + DOCUMENT_JOB_WAIT_MS;
    for (;;) {
        if (!job) throw new Error("Your session has ended. Please sign in again.");
        if (job.status === "succeeded") return job;
        if (job.status === "failed" || job.pandaDocUnavailable) throw new DocumentJobError(job);
        if (Date.now() > deadline) {
            throw new Error("Preparing the document is taking longer than usual. Please try again in a few minutes.");
        }
        await new Promise((resolve) => setTimeout(resolve, DOCUMENT_JOB_POLL_MS));
        job = await apiRequestUnwrapped<DocumentJobInfo>(`/api/document-jobs/${job.jobId}`, { method: "GET" });
    }
}

// ---------- encryption ----------
/** DER length octets for a content of n bytes. */
const derLength = (n: number): number[] =>
    n < 0x80 ? [n] : n < 0x100 ? [0x81, n] : [0x82, n >> 8, n & 0xff];

/**
 * The key as SubjectPublicKeyInfo, the only RSA public-key form WebCrypto imports. pub2025-01.PEM holds a bare PKCS#1
 * RSAPublicKey (SEQUENCE { modulus, exponent }), which is wrapped here; a key that is already SPKI is returned as is.
 */
export const toSpki = (der: Uint8Array<ArrayBuffer>): Uint8Array<ArrayBuffer> => {
    const headerLength = der[1] < 0x80 ? 2 : 2 + (der[1] & 0x7f);
    if (der[0] !== 0x30 || der[headerLength] !== 0x02) {
        return der; // not SEQUENCE { INTEGER ... }: already SPKI
    }
    // AlgorithmIdentifier { rsaEncryption (1.2.840.113549.1.1.1), NULL }
    const algorithm = [0x30, 0x0d, 0x06, 0x09, 0x2a, 0x86, 0x48, 0x86, 0xf7, 0x0d, 0x01, 0x01, 0x01, 0x05, 0x00];
    const bitString = [0x03, ...derLength(der.length + 1), 0x00, ...der];
    const body = [...algorithm, ...bitString];
    return new Uint8Array([0x30, ...derLength(body.length), ...body]);
};

export const encryptAPI = {
    fetchPublicKey: async (): Promise<string> => {
        const pemUrl = absoluteUrl("pub2025-01.PEM");

        const resp = await axios.get(pemUrl, { responseType: "text" });
        const text = String(resp?.data ?? "").trim();

        if (!text) throw new Error(`Empty public key at ${pemUrl}`);
        return text;
    },

    encryptSSN: async (ssn: string): Promise<string> => {
        try {
            const publicKeyPem = await encryptAPI.fetchPublicKey();
            if (!publicKeyPem) {
                throw new Error("Public key is empty");
            }

            // RSA-OAEP with SHA-256 (MGF1 SHA-256 too), matching the server's RSAEncryptionPadding.OaepSHA256 / Key Vault RsaOaep256.
            const base64 = publicKeyPem
                .replace(/-----(BEGIN|END) (RSA )?PUBLIC KEY-----/g, "")
                .replace(/\s+/g, "");
            const der = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
            const publicKey = await crypto.subtle.importKey(
                "spki", toSpki(der), { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
            const encrypted = new Uint8Array(await crypto.subtle.encrypt(
                { name: "RSA-OAEP" }, publicKey, new TextEncoder().encode(ssn)));
            return btoa(String.fromCharCode(...encrypted));
        } catch (error) {
            throw new Error(handleError(error, "Error encrypting SSN:"));
        }
    },
};
// Modify getCurrentUser implementation
export const authApi = {
    login: async (data: LoginRequest): Promise<LoginResponse> => {
      let lRes =  await apiRequest<ApiEnvelope<LoginResponse>>(
            "/api/auth/login",
            { method: "POST", data }
        )
      
        return lRes.data;
    },
    register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    let rRes =    await apiRequest<ApiEnvelope<RegisterResponse>>(
            "/api/auth/register",
            { method: "POST", data, timeout: ACCOUNT_CREATION_TIMEOUT_MS }
        )
        return rRes.data;
    },
    /**
     * Creates the applicant before the password screen so an abandoned registration is still
     * captured. Returns the token that `register` uses to claim the same partial applicant.
     */
    beginRegistration: async (
        data: Omit<RegisterRequest, "password">
    ): Promise<{ registrationToken: string }> =>
        (
            await apiRequest<ApiEnvelope<{ registrationToken: string }>>(
                "/api/auth/begin-registration",
                { method: "POST", data, timeout: ACCOUNT_CREATION_TIMEOUT_MS }
            )
        ).data,
    setPassword: async (
        token: string,
        password: string
    ): Promise<RegisterResponse> =>
        (
            await apiRequest<ApiEnvelope<RegisterResponse>>("/api/auth/set-password", {
                method: "POST",
                data: { token, password },
                timeout: ACCOUNT_CREATION_TIMEOUT_MS,
            })
        ).data,
    /** Masked e-mail of the account a set-password link belongs to (GET /api/auth/set-password-info); rejects for a dead link. */
    getSetPasswordInfo: async (token: string): Promise<{ maskedEmail: string }> =>
        (
            await apiRequest<ApiEnvelope<{ maskedEmail: string }>>(
                `/api/auth/set-password-info?token=${encodeURIComponent(token)}`,
                { method: "GET" }
            )
        ).data,
    checkAccount: async (identifier: string): Promise<CheckAccountResponse> =>
        (
            await apiRequest<ApiEnvelope<CheckAccountResponse>>(
                "/api/auth/check-account",
                { method: "POST", data: { identifier } }
            )
        ).data,
    forgotPassword: async (
        identifier: string
    ): Promise<{ success: boolean; message?: string }> =>
        (
            await apiRequest<
                ApiEnvelope<{ success: boolean; message?: string }>
            >("/api/auth/forgot-password", {
                method: "POST",
                data: { identifier },
            })
        ).data,
    /**
     * Completes a password reset from the emailed link (`POST /api/auth/reset-password`). The token is
     * one-hour and single-use; the password must be at least 8 characters. Does not sign the applicant in.
     * `email` is accepted for the form's display only and is not sent to the server's decision.
     */
    resetPassword: async (
        token: string,
        email: string | undefined,
        password: string
    ): Promise<void> => {
        await apiRequest<unknown>("/api/auth/reset-password", {
            method: "POST",
            data: { token, email, password },
        });
    },
    verifyEmail: async (
        code: string,
        email?: string
    ): Promise<VerifyEmailResponse> =>
        (
            await apiRequest<ApiEnvelope<VerifyEmailResponse>>("/api/auth/verify-email", {
                method: "POST",
                data: { code, ...(email ? { email } : {}) },
            })
        ).data,
    resendVerificationCode: async (
        applicantId: number | undefined,
        email: string
    ): Promise<VerifyEmailResponse> =>
        (
            await apiRequest<ApiEnvelope<VerifyEmailResponse>>(
                "/api/auth/resend-verification",
                { method: "POST", data: { applicantId, email } }
            )
        ).data,
    /** Database-backed "has this e-mail been verified yet" for the signed-in applicant (the link may be opened in another browser). */
    getVerificationStatus: async (): Promise<boolean> => {
        const response = await apiRequest<ApiEnvelope<{ verified: boolean }>>("/api/auth/verification-status", { method: "GET" });
        return response?.data?.verified === true;
    },
    getCurrentUser: async (): Promise<User | null> => {
        try {
            const response = await apiRequest<ApiEnvelope<UserResponse>>("/api/auth/me", { method: "GET" });

            // No response or server explicitly says not authenticated
            if (!response || response.success === false) return null;

            // Extract the user data from the API envelope
            const userData = response.data;

            // Guard: must have a valid applicantId to be a real user
            if (!userData || !userData.applicantId) return null;

            //Call User.fromJSON() to trigger the JSON parsing transform
            return User.fromJSON(userData);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                return null; // ignore 401
            }
            console.error("getCurrentUser error:", error);
            return null;
        }
    },
    getAuthStatus: async (): Promise<AuthStatus | undefined> => {
        try {
            return await apiRequest<AuthStatus>("/api/auth/auth-status", {
                method: "GET",
            });
        } catch (e) {
            console.error("Error checking authentication status:", e);
            return undefined;
        }
    },
    logout: async (logoutCallback: () => void): Promise<void> => {
        try {
            await apiRequest<void>("/api/auth/logout", { method: "POST" });
        } catch (e) {
            console.error(
                "Logout API call failed:",
                (e as Error)?.message ?? e
            );
        } finally {
            logoutCallback();
        }
    },
    resetFirstVisit: async (): Promise<void> =>
        apiRequest<void>("/api/auth/reset-first-visit", { method: "POST" }),
    updateFirstVisit: async (): Promise<void> =>
        apiRequest<void>("/api/auth/update-first-visit", { method: "POST" }),
    /** Passwordless @testsep.com sign-in; the server only maps it on a local Development host. */
    autoAuthenticate: async (email: string): Promise<unknown> =>
        apiRequestUnwrapped<unknown>("/api/auth/auto-authenticate", {
            method: "POST",
            data: { email },
        }),
    autoAuthenticateDump: async (email: string): Promise<unknown> =>
        apiRequestUnwrapped<unknown>("/api/auth/auto-authenticate-dump", {
            method: "POST",
            data: { email },
        }),
    getTestUsers: async (): Promise<TestUser[]> =>
        (
            await apiRequest<ApiEnvelope<TestUser[]>>("/api/auth/test-users", {
                method: "GET",
                timeout: 60000, // ? Add 60 second timeout
            })
        ).data,
};

export interface IHighSchoolInfo
{
    /**
     * dbo.AmHighSchool_VW.AMHighSchoolID. Mirrors ICollegeInfo.AmCollegeId below.
     *
     * The API used to return only the name/city/state, so a selected high school could never be
     * identified by anything but its display string. That left HighSchoolInformation.amHighSchoolId
     * unpopulated, which in turn made the Nexus ProspectPreviousEducations sync send
     * HighSchoolId = null and left the document datapoints that resolve through
     * ApplicantSecondaryEducationDetail.AmHighSchool (high school city / state / zip) blank.
     *
     * Note the source view holds duplicate rows per school; the server collapses them and returns the
     * lowest id for each name/city/state group.
     */
    AmHighSchoolId: number;
    Name: string;
    City: string;
    State: string;
}

export interface ICollegeInfo
{
    AmCollegeId: number;
    Name: string;
    City: string;
    State: string;
}

export interface INewSchoolInfo
{
    Name: string,
    Address: string,
    City: string,
    County: string,
    State: string,
    Zip: string,
    SchoolType: string
}

export interface ITechCheckInfo
{
    filename: string,
    file: File | null,
    meetsRequirements: boolean | null
}

export interface IMilitaryBaseInfo
{
    Installation: string;
    Branch: string;
    InstallationID: number;
}


export interface ICollegeData
{
    applicantPostSecondaryId?: number;
    name: string;
    orderForMe?: boolean;
    studentRequested?: boolean;
    orderStatus?: number;
    /** True once the transcript request has actually been sent (ApplicantPostSecondary.DateReq is set). */
    requestSent?: boolean;
    /** True once the latest official transcript evaluation request is complete (TranscriptEvalSummary_VW, by SyStudentId). */
    evaluated?: boolean;
    unofficialTranscript?: TransferCreditsFileData;
}

//TODO: Organize
export interface ITransferCreditsFileData extends IFileUploadData
{
    // camelCase to match the server JSON (TransferCreditsUpload). Persist as an ISO timestamp so the
    // JSON payload binds cleanly to the server's DateTime property.
    fileType: string;
    dateUploaded: string;
}

export class TransferCreditsFileData extends FileUploadData
{
    fileType: string;
    dateUploaded: string;

    constructor(data?: ITransferCreditsFileData | null)
    {
        super(data);

        if (data === null || typeof data === "undefined")
        {
            this.fileType = "";
            this.dateUploaded = new Date().toISOString();
            return;
        }

        this.fileType = data.fileType;
        this.dateUploaded = data.dateUploaded;
    }
}
export interface ITransferCreditsProps
{
    colleges: ICollegeData[];
    enrolled: boolean;
    applicationFeePaid?: boolean;
    plaFiles: TransferCreditsFileData[];
    plaValue: string;
    showPla: boolean;
    alreadySubmitted: boolean;
}

// ---------- Data API ----------
export const dataApi = {
    getCampusLocations: async (): Promise<CampusLocation[]> => {
        const envelope = await apiRequest<ApiEnvelope<CampusLocation[] | CampusLocation>>(
            "/api/auth/campus-locations",
            { method: "GET" }
        );
        const raw = envelope && (envelope as ApiEnvelope<CampusLocation[] | CampusLocation>).data;
        if (Array.isArray(raw)) return raw;
        return raw ? [raw] : [];
    },
    getPrograms: async (): Promise<Program[]> => {
        const envelope = await apiRequest<ApiEnvelope<Program[]>>("/api/auth/programs", {
            method: "GET",
        });
        return envelope?.data ?? [];
    },
    storeSecureData: async (
        category: string,
        request: SecureDataRequest
    ): Promise<StoreSecureDataResponse> => {
        try {
            const res = await apiRequestProblem<StoreSecureDataResponse>("/api/SecureData/store", {
                method: "POST",
                params: { category },
                data: request,
            });
            return res;
        } catch (err) {
            if (err instanceof ApiProblemError) {
                // 422 means draft was saved but completion check failed � surface the result
                const detail = err.details as { saved?: boolean; result?: FormValidationResult } | undefined;
                return {
                    success: false,
                    saved: detail?.saved ?? true,
                    result: detail?.result ?? {
                        saved: detail?.saved ?? true,
                        isComplete: false,
                        fieldErrors: (err.fieldErrors as Record<string, string[]>) ?? {},
                        incompleteReason: err.message,
                    },
                };
            }
            throw err;
        }
    },
    getHighSchoolOptions: async (): Promise<string> =>
        (await apiRequest<string>("/api/auth/highschool-options", { method: "GET" })) ?? [],

    getCollegeOptions: async (): Promise<string> =>
        (await apiRequest<string>("/api/auth/college-options", { method: "GET" })) ?? [],

    getMilitaryBaseOptions: async (): Promise<string> =>
        (await apiRequest<string>("/api/auth/military-base-options", { method: "GET" })) ?? [],

    // Fetch the latest JsonPost for a given category from the server (authoritative)
    // merge: one object built from the recent posts (newest non-empty value per key), for forms whose earlier saves held only
    // the changed fields.
    getLatestJsonPostForCategory: async (category: string, requiredKey?: string, merge = false): Promise<string | null> =>
        apiRequestUnwrapped<string | null>(
            `/api/SecureData/latest?category=${encodeURIComponent(category)}` +
            (requiredKey ? `&requiredKey=${encodeURIComponent(requiredKey)}` : '') +
            (merge ? '&merge=true' : ''),
            { method: 'GET' })
            .then((r) => r ?? null)
            .catch(() => null),

    getTransferCreditsData: async (): Promise<ITransferCreditsProps> =>
        (await apiRequest<ITransferCreditsProps>("/api/TransferCredits/fetch", { method: "GET" })) ?? [],

    getFirstUncompletedPage: async (): Promise<string> =>
        (await apiRequest<string>("/api/auth/first-uncompleted-page", { method: "GET" })) ?? [],

    /**
     * Returns the applicant's completed page/step GUIDs, used by "continue from last
     * panel" so a page can resume at its first uncompleted step. Distinct from
     * getFirstUncompletedPage (which is workflow-level, not intra-page).
     */
    getCompletedSteps: async (): Promise<string[]> =>
        (await apiRequest<string[]>("/api/progress/completed-steps", { method: "GET" })) ?? [],

    /**
     * Returns the Personal Profile pages that hold saved data but are not complete (the high school and college pages when
     * the student moved on without signing the transcript request). The side menu marks them "saved, not finished".
     */
    getIncompleteSteps: async (): Promise<string[]> =>
        (await apiRequest<string[]>("/api/progress/incomplete-steps", { method: "GET" })) ?? [],

    /**
     * Returns whether the one-time "Profile Complete" dashboard modal has already been
     * shown to the applicant. Used to ensure it is only ever displayed once.
     */
    getProfileCompleteModalSeen: async (): Promise<boolean> =>
        (await apiRequest<{ seen: boolean }>("/api/progress/profile-complete-modal-seen", { method: "GET" }))?.seen ?? false,

    /**
     * Marks the one-time "Profile Complete" dashboard modal as seen so it is never
     * displayed again.
     */
    markProfileCompleteModalSeen: async (): Promise<void> => {
        await apiRequest<void>("/api/progress/profile-complete-modal-seen", { method: "POST" });
    },

    submitNewSchool: async (
        data: INewSchoolInfo
    ): Promise<string> =>
        (await apiRequest<string>("/api/auth/submitNewSchool", {
            method: "POST",
            data: data,
        })) ?? "",
    submitTechCheck: async (
            data: ITechCheckInfo
        ): Promise<string> =>
        {
            const formData = new FormData();
            formData.append("filename", data.filename);
            formData.append("meetsRequirements", String(data.meetsRequirements));

            if (data.file)
            {
                formData.append("file", data.file);
            }

            const result = await apiRequest<string>("/api/auth/submitTechCheck", {
                method: "POST",
                data: formData,
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // apiRequest resolves to null on 401 rather than throwing; treat it as a failed submit
            if (result === null)
            {
                throw new Error("Technology Check submission was not authorized.");
            }

            return result ?? "";
        },

    submitTransferCredits: async (
        data: ITransferCreditsProps
    ): Promise<string> =>
    {
        const request = {
            ...data,
            colleges: data.colleges.map(college => ({
                ...college,
                unofficialTranscript: college.unofficialTranscript
                    ? {
                        filename: college.unofficialTranscript.filename,
                        alreadyUploaded: college.unofficialTranscript.alreadyUploaded,
                    }
                    : undefined,
            })),
            plaFiles: data.plaFiles.map(file => ({
                filename: file.filename,
                alreadyUploaded: file.alreadyUploaded,
                fileType: file.fileType,
                dateUploaded: file.dateUploaded,
            })),
        };

        return (await apiRequest<string>("/api/TransferCredits/save", {
            method: "POST",
            data: request,
        })) ?? "";
    },

    /**
     * Uploads a student-provided unofficial transcript for one college and links it to that
     * ApplicantPostSecondary. The college must already be saved (have an applicantPostSecondaryId).
     * Returns success:false when no upload slot is available for the applicant.
     */
    uploadUnofficialTranscript: async (
        applicantId: number,
        applicantPostSecondaryId: number,
        file: File
    ): Promise<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }> =>
    {
        const formData = new FormData();
        formData.append("applicantPostSecondaryId", String(applicantPostSecondaryId));
        formData.append("file", file);

        // IMPORTANT: send Content-Type as undefined so axios/the browser set
        // "multipart/form-data; boundary=…" automatically. Setting it explicitly to
        // "multipart/form-data" omits the boundary, which makes the server unable to parse
        // the body (File binds to null). apiRequest's `...options` spread replaces the merged
        // headers with exactly what we pass here, so undefined is what reaches axios.
        return (await apiRequestUnwrapped<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }>(
            `/api/documents/${applicantId}/unofficial-transcript`,
            {
                method: "POST",
                data: formData,
                headers: { "Content-Type": undefined } as any,
            }
        )) ?? { success: false };
    },

    /** The unofficial transcript linked to each of the applicant's colleges (restores the uploaded file on return). */
    getUnofficialTranscripts: async (
        applicantId: number
    ): Promise<Array<{ applicantPostSecondaryId: number; applicantDocumentId: number; fileName: string }>> =>
        (await apiRequestUnwrapped<Array<{ applicantPostSecondaryId: number; applicantDocumentId: number; fileName: string }>>(
            `/api/documents/${applicantId}/unofficial-transcripts`,
            { method: "GET" }
        )) ?? [],

    /**
     * Removes a student-provided unofficial transcript for one college by unlinking it from that
     * ApplicantPostSecondary, freeing the slot for a re-upload. Returns success:false when there was
     * nothing linked to remove.
     */
    removeUnofficialTranscript: async (
        applicantId: number,
        applicantPostSecondaryId: number
    ): Promise<{ success: boolean; message?: string }> =>
        (await apiRequestUnwrapped<{ success: boolean; message?: string }>(
            `/api/documents/${applicantId}/unofficial-transcript/${applicantPostSecondaryId}`,
            { method: "DELETE" }
        )) ?? { success: false },

    /** Whether the applicant has an unofficial military transcript on file. */
    getMilitaryTranscript: async (
        applicantId: number
    ): Promise<{ uploaded: boolean; fileName?: string; dateAdded?: string }> =>
        (await apiRequestUnwrapped<{ uploaded: boolean; fileName?: string; dateAdded?: string }>(
            `/api/documents/${applicantId}/military-transcript`,
            { method: "GET" }
        )) ?? { uploaded: false },

    /** Uploads the applicant's unofficial military transcript (stored as a new ApplicantDocumentVersion). */
    uploadMilitaryTranscript: async (
        applicantId: number,
        file: File
    ): Promise<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }> =>
    {
        const formData = new FormData();
        formData.append("file", file);

        // See uploadUnofficialTranscript: Content-Type undefined so the browser sets the multipart boundary.
        return (await apiRequestUnwrapped<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }>(
            `/api/documents/${applicantId}/military-transcript`,
            {
                method: "POST",
                data: formData,
                headers: { "Content-Type": undefined } as any,
            }
        )) ?? { success: false };
    },

    /** Removes the applicant's unofficial military transcript. */
    removeMilitaryTranscript: async (
        applicantId: number
    ): Promise<{ success: boolean; message?: string }> =>
        (await apiRequestUnwrapped<{ success: boolean; message?: string }>(
            `/api/documents/${applicantId}/military-transcript`,
            { method: "DELETE" }
        )) ?? { success: false },

    /**
     * Uploads a Prior Learning Assessment (PLA) document for the applicant and stores it as a new
     * ApplicantDocumentVersion. The server maps docType to the PLA document: "resume" -> resume doc
     * (dev 307 / prod 324); "cert" or "trainings" -> certification/training doc (dev 308 / prod 325).
     */
    uploadPlaDocument: async (
        applicantId: number,
        docType: string,
        file: File
    ): Promise<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }> =>
    {
        const formData = new FormData();
        formData.append("docType", docType);
        formData.append("file", file);

        // See uploadUnofficialTranscript: send Content-Type undefined so the browser sets the
        // multipart boundary itself.
        return (await apiRequestUnwrapped<{ success: boolean; applicantDocumentId?: number; fileName?: string; message?: string }>(
            `/api/documents/${applicantId}/pla`,
            {
                method: "POST",
                data: formData,
                headers: { "Content-Type": undefined } as any,
            }
        )) ?? { success: false };
    },
    /** Removes the applicant's uploaded PLA document for the given docType ("resume", "cert", "trainings"). */
    removePlaDocument: async (
        applicantId: number,
        docType: string
    ): Promise<{ success: boolean; message?: string }> =>
        (await apiRequestUnwrapped<{ success: boolean; message?: string }>(
            `/api/documents/${applicantId}/pla/${encodeURIComponent(docType)}`,
            { method: "DELETE" }
        )) ?? { success: false },

    /**
 * Get all documents for a student by applicantId
 * @param applicantId - The applicant ID
 * @returns Promise with student documents
 */
getStudentDocuments: async (applicantId: number): Promise<IStudentDocumentsResponse> => {
    return await apiRequest<IStudentDocumentsResponse>(
        `/api/documents/student/${applicantId}`,
        { method: 'GET' }
    );
},

/**
 * Check if a student's high school transcript has been received
 * @param applicantId - The applicant ID
 * @returns Promise with transcript status
 */
getHighSchoolTranscriptStatus: async (applicantId: number): Promise<ITranscriptStatusResponse> => {
    return await apiRequest<ITranscriptStatusResponse>(
        `/api/documents/student/${applicantId}/highSchoolTranscript`,
        { method: 'GET' }
    );
},

    /**
     * Get the education type for a student by applicantId
     * @param applicantId - The applicant ID
     * @returns Promise with education type
     */
    getEducationType: async (applicantId: number): Promise<IEducationTypeResponse> => {
        return await apiRequest<IEducationTypeResponse>(
            `/api/documents/student/${applicantId}/education-type`,
            { method: 'GET' }
        );
    },

    /**
     * Get the transcript due date information for a student
     * @param applicantId - The applicant ID
     * @returns Promise with due date information
     */
    getTranscriptDueDate: async (applicantId: number): Promise<ITranscriptDueDateResponse> => {
        return await apiRequest<ITranscriptDueDateResponse>(
            `/api/documents/student/${applicantId}/transcript-due-date`,
            { method: 'GET' }
        );
    },

    /**
     * Get the campus address for a student's enrollment
     * @param applicantId - The applicant ID
     * @returns Promise with campus address information
     */
    getCampusAddress: async (applicantId: number): Promise<ICampusAddressResponse> => {
        try {
            const response = await apiRequest<ICampusAddressResponse>(
                `/api/documents/student/${applicantId}/campus-address`,
                { method: 'GET' }
            );
            
            
            return response;
        } catch (error) {
            console.error('[getCampusAddress] Error fetching campus address:', {
                applicantId,
                error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
        }
    },

    getEmploymentInfo: async (): Promise<EmploymentInfoFormData> =>
    {
        try
        {
            const response = await apiRequest<EmploymentInfoFormData>(
                `/api/PersonalProfile/GetEmploymentData`,
                { method: 'GET' }
            );

            for (const data of (response?.positionData ?? []))
            {
                //These are somehow getting set to strings despite being specified as dates, so we fix that here.
                if (typeof data.employmentStart === "string")
                {
                    data.employmentStart = new Date(data.employmentStart as string);
                }
            }

            return response;
        } catch (error)
        {
            console.error('[getEmploymentInfo] Error fetching employment data:', {
                error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
        }
    },

    getAdvisorContact: async (): Promise<{ campusId: number | null; campusName: string | null; phone: string | null; hasAdvisor: boolean }> => {
        const response = await httpGet<{ campusId: number | null; campusName: string | null; phone: string | null; hasAdvisor: boolean }>(
            `/api/Campus/advisor-contact`
        );
        return response.data ?? { campusId: null, campusName: null, phone: null, hasAdvisor: true };
    },

    isAtiCampus: async (campusId: number): Promise<number> => {
        try {
            const response = await httpGet<number>(`/api/Campus/${campusId}/is-ati-campus`);
            return response.data ?? 0;
        } catch (error) {
            console.error('[isAtiCampus] Error checking campus type:', {
                error,
                errorMessage: error instanceof Error ? error.message : 'Unknown error'
            });
            throw error;
        }
    },
};

// ---------- Profile Form API � typed per-step endpoints with SecureData fallback ----------
/**
 * Maps each profile step's category key to the typed endpoint on PersonalProfileController.
 */
const TYPED_SAVE_ENDPOINTS: Record<string, string> = {
    yourinformation:             "/api/PersonalProfile/SavePersonalInfo",
    addressinformation:          "/api/PersonalProfile/SaveAddressInfo",
    studentdetails:              "/api/PersonalProfile/SaveStudentDetails",
    militaryinformation:         "/api/PersonalProfile/SaveMilitaryInfo",
    // highschoolinformation and collegeeducationinformation are intentionally absent:
    // their handlers delegate to usp_UpsertFromJsonPost which requires a real ProcessRequest
    // row (postId > 0) that only the SecureData/store fallback path creates.
};

/**
 * Saves a profile step by first calling the typed endpoint on PersonalProfileController.
 * Falls back to the generic SecureData/store path if the typed call fails for any reason.
 *
 * @param category  Lowercase category key (e.g. "yourinformation").
 * @param payload   The serialisable payload object to send to the typed endpoint.
 * @param request   The SecureDataRequest used only for the fallback path.
 */
export async function saveProfileStep(
    category: string,
    payload: Record<string, unknown>,
    request: SecureDataRequest
): Promise<StoreSecureDataResponse> {
    const endpoint = TYPED_SAVE_ENDPOINTS[category.toLowerCase()];

    if (endpoint) {
        try {
            await apiRequest<void>(endpoint, {
                method: "POST",
                data: payload,
            });
            return {
                success: true,
                saved: true,
                result: { saved: true, isComplete: true, fieldErrors: {} },
            };
        } catch (err) {
            console.warn(
                `[saveProfileStep] Typed endpoint ${endpoint} failed � falling back to SecureData/store`,
                err
            );
        }
    }

    // Fallback: generic secure-data path
    return dataApi.storeSecureData(category, request);
}

/**
 * Saves emergency contact information via the typed PersonalProfileController
 * endpoint. Used by the Program Enrollment workflow to persist the Emergency
 * Contact panel and mark it complete for "continue from last panel".
 */
export async function saveEmergencyContact(
    payload: Record<string, unknown>
): Promise<void> {
    await apiRequest<void>("/api/PersonalProfile/SaveEmergencyContact", {
        method: "POST",
        data: payload,
    });
}

/** The emergency contact as the server stored it (camelCase), or null when none is saved yet. */
export interface SavedEmergencyContact {
    firstName?: string | null;
    lastName?: string | null;
    relationToStudent?: string | null;
    phoneNumber?: string | null;
    secondPhone?: string | null;
    email?: string | null;
    streetAddress?: string | null;
    streetAddressLine2?: string | null;
    city?: string | null;
    stateCode?: string | null;
    postalCode?: string | null;
    countryID?: number | null;
}

/** Loads the saved emergency contact so the form reopens with it. */
export async function getEmergencyContact(): Promise<SavedEmergencyContact | null> {
    const body = await apiRequest<SavedEmergencyContact | "" | null>("/api/PersonalProfile/GetEmergencyContact", {
        method: "GET",
    });
    return body && typeof body === "object" ? body : null;
}

/** The mailing address as the server stored it, or null when none is saved yet. */
export interface SavedMailingAddress {
    sameAsHome: boolean;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    countryId?: number | null;
}

/** One Degree Level choice for the College Information form: `label` is shown, `value` is what is saved. */
export interface DegreeLevelOption {
    value: string;
    label: string;
}

/** The Degree Level dropdown's choices, in display order (from the CollegeDegreeLevel_VW view). */
export async function getCollegeDegreeLevels(): Promise<DegreeLevelOption[]> {
    const body = await apiRequest<DegreeLevelOption[] | null>("/api/PersonalProfile/GetCollegeDegreeLevels", {
        method: "GET",
    });
    return Array.isArray(body) ? body : [];
}

/** Loads the saved mailing address so the address form reopens with it (it was never stored before). */
export async function getMailingAddress(): Promise<SavedMailingAddress | null> {
    const body = await apiRequest<SavedMailingAddress | "" | null>("/api/PersonalProfile/GetMailingAddress", {
        method: "GET",
    });
    return body && typeof body === "object" ? body : null;
}

/**
 * Marks a Financial workflow panel complete for "continue from last panel".
 * The server only honors known Financial panel GUIDs; callers treat failures
 * as non-fatal so the UI flow can advance regardless of persistence outcome.
 */
export async function markFinancialStepComplete(panelId: string): Promise<void> {
    await apiRequest<void>("/api/Financial/complete-step", {
        method: "POST",
        data: { panelId },
    });
}

/**
 * After a successful college save, returns the DB-assigned ApplicantPostSecondaryId for
 * every post-secondary row belonging to the current applicant, keyed by nameWhileEnrolled.
 * Used to patch the in-memory institution list so subsequent saves match existing rows
 * instead of inserting duplicates.
 */
export async function getPostSecondaryIds(): Promise<{
    applicantPostSecondaryId: number;
    amCollegeId: number | null;
    applicantCollegeId: number | null;
    collegeName: string | null;
    applicantSchoolId: number | null;
    schoolName: string | null;
}[]> {
    // The server answers applicantSchoolId / schoolName (the college rows moved to ApplicantSchool); callers match on
    // applicantCollegeId / collegeName. Without this mapping a manually entered college never got its saved id, so its
    // Review & Sign and transcript upload failed and each save inserted it again (UAT).
    const rows = await apiRequest<Array<{
        applicantPostSecondaryId: number;
        amCollegeId: number | null;
        applicantSchoolId?: number | null;
        schoolName?: string | null;
        applicantCollegeId?: number | null;
        collegeName?: string | null;
    }>>("/api/PersonalProfile/GetPostSecondaryIds", { method: "GET" });

    return (rows ?? []).map((r) => {
        const applicantSchoolId = r.applicantSchoolId ?? r.applicantCollegeId ?? null;
        const schoolName = r.schoolName ?? r.collegeName ?? null;
        return {
            applicantPostSecondaryId: r.applicantPostSecondaryId,
            amCollegeId: r.amCollegeId,
            applicantCollegeId: applicantSchoolId,
            collegeName: schoolName,
            applicantSchoolId,
            schoolName,
        };
    });
}

// ---------- Demo / Debug ----------
export const profileFormApi = {
    /**
     * Saves a draft � always persists, returns format errors but never blocks.
     * Call on field blur / auto-save (checkCompletion: false).
     */
    saveDraft: async (
        category: string,
        fields: Record<string, unknown>
    ): Promise<StoreSecureDataResponse> =>
        dataApi.storeSecureData(category, {
            jsonData: JSON.stringify(fields),
            checkCompletion: false,
        }),

    /**
     * Saves a draft AND runs the full completion check.
     * Call on "Next" / "Continue" button press.
     * Returns 422 (mapped to a resolved StoreSecureDataResponse) when required fields are missing.
     */
    validateAndSave: async (
        category: string,
        fields: Record<string, unknown>
    ): Promise<StoreSecureDataResponse> =>
        dataApi.storeSecureData(category, {
            jsonData: JSON.stringify(fields),
            checkCompletion: true,
        }),

    /**
     * Returns server-authoritative completion status for all profile form sections.
     */
    getCompletionStatus: async (): Promise<Record<string, boolean>> => {
        const res = await apiRequest<{ success: boolean; data: Record<string, boolean> }>(
            "/api/ProfileForm/completion-status",
            { method: "GET" }
        );
        return res?.data ?? {};
    },
};

// ---------- Demo / Debug ----------
export const demoAPI = {
    devAutoVerify: async (to: string): Promise<boolean> =>
        (
            await apiRequest<ApiEnvelope<boolean>>("/demo/verify-email-dev", {
                method: "POST",
                params: { email: to, verificationCode: "dev-auto-verify" },
            })
        ).success,
    debugInfo: async (): Promise<Record<string, unknown>> =>
        apiRequest<Record<string, unknown>>("/demo/debug-info", {
            method: "GET",
        }),
};

// ---------- Enrollment API (consolidated) ----------
export const enrollmentApi = {
    saveProfile: async <T = Record<string, unknown>>(
        profileData: T
    ): Promise<unknown> =>
        apiRequest<unknown>("/api/profile", {
            method: "POST",
            data: profileData,
        }),
    submitApplication: async <T = Record<string, unknown>>(
        applicationData: T
    ): Promise<unknown> =>
        apiRequest<unknown>("/api/enrollment/submit", {
            method: "POST",
            data: applicationData,
        }),
    uploadDocument: async (formData: FormData): Promise<unknown> =>
        apiRequest<unknown>("/api/documents/upload", {
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
        }),
    updatePaymentMethod: async <T = Record<string, unknown>>(
        paymentData: T
    ): Promise<unknown> =>
        apiRequest<unknown>("/api/payment/method", {
            method: "PUT",
            data: paymentData,
        }),
    getProgramSelection: async (applicantId: number): Promise<{
        adDegreeId: string;
        adCollegeId?: string;
        adProgramId: string;
        adProgramVersionId: string;
        adTermId: string;
    }> =>
        apiRequestUnwrapped<{
            adDegreeId: string;
            adCollegeId?: string;
            adProgramId: string;
            adProgramVersionId: string;
            adTermId: string;
        }>(`/api/enrollment/program-selection/${applicantId}`, {
            method: "GET",
        }),
    getApplicantEnrollmentId: async (applicantId: number): Promise<number | null> =>
        apiRequestUnwrapped<number | null>(`/api/enrollment/applicant/${applicantId}/enrollment-id`, {
            method: "GET",
            timeout: 120000, // ? Increase to 2 minutes
        }),
    // Program Enrollment (Enrollment Information) dashboard status, sourced from
    // Applicant.DashboardStatus_VW (ApplicantDashboardStatusVw.ProgramEnrollmentStatus).
    // Returns the raw view string ("Not started" | "In Progress" | "Completed"), or null
    // when no row exists yet.
    getEnrollmentInformationStatus: async (applicantId: number): Promise<string | null> =>
        apiRequestUnwrapped<{ status: string | null }>(
            `/api/enrollment/applicant/${applicantId}/enrollment-status`,
            {
                method: "GET",
                timeout: 120000,
            }
        ).then((r) => r?.status ?? null),
    getApplicantEnrollmentDetails: async (applicantEnrollmentId: number): Promise<{
        adDegreeId: string;
        adCollegeId?: string;
        adProgramId: string;
        adProgramVersionId: string;
        adTermId: string;
    }> =>
        apiRequestUnwrapped<{
            adDegreeId: string;
            adCollegeId?: string;
            adProgramId: string;
            adProgramVersionId: string;
            adTermId: string;
        }>(`/api/enrollment/applicant-enrollment/${applicantEnrollmentId}`, {
            method: "GET",
        }),
    getEnrollmentFees: async (applicantEnrollmentId: number): Promise<any[]> =>
        apiRequestUnwrapped<any[]>(`/api/enrollment/enrollment/${applicantEnrollmentId}/fees`, {
            method: "GET",
            timeout: 120000, // ? Increase to 120 seconds (2 minutes)
        }),
    // Financial Assistance dashboard tile status, sourced from Applicant.DashboardStatus_VW
    // (ApplicantDashboardStatusVw.FinancialAssistanceStatus). Returns the raw view string
    // ("Not started" | "In Progress" | "Completed"), or null when no row exists yet.
    getFinancialAssistanceStatus: async (applicantId: number): Promise<string | null> =>
        apiRequestUnwrapped<{ status: string | null }>(
            `/api/financial/financial-assistance-status?applicantId=${applicantId}`,
            { method: "GET" }
        ).then((r) => r?.status ?? null),
    // Whether the applicant has completed at least one Financial workflow panel (CompletedPage
    // rows for Financial panels). Lets the dashboard show a "Started" status while the slower-
    // updating Reach FinancialAssistanceStatus still reports "Not started". Returns false on error.
    getFinancialPanelsStarted: async (applicantId: number): Promise<boolean> =>
        apiRequestUnwrapped<{ hasStarted: boolean }>(
            `/api/financial/financial-panels-started?applicantId=${applicantId}`,
            { method: "GET" }
        ).then((r) => r?.hasStarted ?? false),
    // The same endpoint with currentActionsComplete: every Financial step the student can do now is done (the loan steps
    // only once FAFSA results exist), so the dashboard shows Pending instead of In Progress.
    getFinancialPanelsProgress: async (applicantId: number): Promise<{ hasStarted: boolean; currentActionsComplete: boolean }> =>
        apiRequestUnwrapped<{ hasStarted: boolean; currentActionsComplete?: boolean }>(
            `/api/financial/financial-panels-started?applicantId=${applicantId}`,
            { method: "GET" }
        ).then((r) => ({ hasStarted: r?.hasStarted ?? false, currentActionsComplete: r?.currentActionsComplete ?? false })),
    // Transfer Credits dashboard tile status, sourced from Applicant.DashboardStatus_VW
    // (ApplicantDashboardStatusVw.TransferCreditStatus). Returns the raw view string
    // ("In Progress" | "Not Started"), or null when no row exists yet. The endpoint reads
    // the applicant from the auth claim, so no id is passed.
    getTransferCreditsStatus: async (): Promise<string | null> =>
        apiRequestUnwrapped<{ status: string | null }>(
            `/api/TransferCredits/transfer-credit-status`,
            { method: "GET" }
        ).then((r) => r?.status ?? null),
    createEnrollmentFeesForEnrollment: async (applicantEnrollmentId: number): Promise<{ success: boolean; message: string }> =>
        apiRequestUnwrapped<{ success: boolean; message: string }>(`/api/enrollment/enrollment/${applicantEnrollmentId}/fees`, {
            method: "POST",
            timeout: 100000, // 100 seconds for fee creation
        }),
    getEnrollmentDocuments: async (applicantId: number): Promise<EnrollmentDocumentsInfoDto> => {
        return apiRequestUnwrapped<EnrollmentDocumentsInfoDto>(`/api/documents/${applicantId}/enrollment`, {
            method: "GET",
            timeout: 120000, // ? Increase to 120 seconds (2 minutes)
        })
    },
    getMilitaryDocuments: async (applicantId: number): Promise<MilitaryDocumentsStatus> =>
        apiRequestUnwrapped<MilitaryDocumentsStatus>(`/api/documents/${applicantId}/military`, {
            method: "GET",
        }),
    uploadMilitaryDocument: async (
        applicantId: number,
        docType: MilitaryDocumentType,
        file: File
    ): Promise<DocumentUploadStatus> => {
        const formData = new FormData();
        formData.append("file", file);

        return apiRequestUnwrapped<DocumentUploadStatus>(`/api/documents/${applicantId}/military/${docType}`, {
            method: "POST",
            data: formData,
            headers: { "Content-Type": "multipart/form-data" },
        });
    },
    getMilitaryDocumentPreviewUrl: (applicantId: number, docType: MilitaryDocumentType): string =>
        `${getApiBaseUrl()}/api/documents/${applicantId}/military/${docType}/preview`,
    getTechCheckStatus: async (applicantId: number): Promise<TechCheckStatus | null> =>
        apiRequestUnwrapped<TechCheckStatus | null>(`/api/documents/${applicantId}/techcheck`, {
            method: "GET",
        }),
    getTechCheckApplicable: async (applicantId: number): Promise<{ applicable: boolean }> =>
        apiRequestUnwrapped<{ applicable: boolean }>(`/api/documents/${applicantId}/techcheck/applicable`, {
            method: "GET",
        }),
    getTechCheckPreviewUrl: (applicantId: number): string =>
        `${getApiBaseUrl()}/api/documents/${applicantId}/techcheck/preview`,
    /**
     * Opens one of the applicant's documents. A step document opened for the first time is prepared in the background
     * (the server answers 202 with a document job): this waits for the job, then asks again.
     */
    getApplicantDocument: async (applicantDocumentId: number, applicantStepId?: number): Promise<DocFile> => {
        const path = applicantStepId
            ? `/api/documents/${applicantDocumentId}/${applicantStepId}`
            : `/api/documents/${applicantDocumentId}`;
        const read = () => apiRequestUnwrapped<DocFile | DocumentJobInfo>(path, {
            method: "GET",
            timeout: 60000, // opening an existing document may create a PandaDoc signing session
        });

        const first = await read();
        if (!isDocumentJob(first)) return first;
        await waitForDocumentJob(first);
        const ready = await read();
        if (isDocumentJob(ready)) throw new DocumentJobError(ready);
        return ready;
    },
    /**
     * Prepares the "Official Transcript Request Authorization" for e-signature: selects the correct
     * authorization document from the education type (Home School vs HS/GED). Returns the document when it
     * is already in PandaDoc; otherwise the server prepares it in the background and returns a
     * DocumentJobInfo to wait on (useTranscriptSigning does that). `required` is false when the education
     * type needs no authorization (e.g. "Did Not Graduate").
     * @param applicantId - The applicant ID
     * @param educationType - The applicant's secondary-education type (e.g. "Home School Diploma", "GED")
     */
    requestTranscriptAuthorization: async (applicantId: number, educationType: string): Promise<DocFile | DocumentJobInfo> =>
        apiRequestUnwrapped<DocFile | DocumentJobInfo>(`/api/documents/${applicantId}/transcript-authorization`, {
            method: "POST",
            data: { educationType },
            timeout: 60000, // returns at once when queued; opening an existing document creates a signing session
        }),
    /**
     * Prepares a per-college "College Transcript Request Authorization" for e-signature and returns
     * the applicantDocumentId to open via getApplicantDocument. `required` is false when the college
     * template isn't configured yet.
     * @param applicantId - The applicant ID
     * @param applicantPostSecondaryId - The target college's ApplicantPostSecondaryId
     */
    requestCollegeTranscriptAuthorization: async (applicantId: number, applicantPostSecondaryId: number): Promise<DocFile | DocumentJobInfo> =>
        apiRequestUnwrapped<DocFile | DocumentJobInfo>(`/api/documents/${applicantId}/college-transcript-authorization`, {
            method: "POST",
            data: { applicantPostSecondaryId },
            timeout: 60000, // returns at once when queued; opening an existing document creates a signing session
        }),
    /**
     * Returns per-college transcript-authorization signing status so the Post-Secondary form can
     * restore "signed" state on reload (the colleges load JSON doesn't carry it).
     * @param applicantId - The applicant ID
     */
    getCollegeTranscriptStatus: async (applicantId: number): Promise<Array<{ applicantPostSecondaryId: number; applicantDocumentId: number; status: string | null; signed: boolean }>> =>
        apiRequestUnwrapped<Array<{ applicantPostSecondaryId: number; applicantDocumentId: number; status: string | null; signed: boolean }>>(`/api/documents/${applicantId}/college-transcript-status`, {
            method: "GET",
            timeout: 60000,
        }),
    /**
     * Permanently deletes one of the applicant's saved colleges (ApplicantPostSecondary). Any linked
     * transcript documents are retained server-side; a manual-entry school is cleaned up when unused.
     * Returns success:false when the record no longer exists.
     * @param applicantId - The applicant ID
     * @param applicantPostSecondaryId - The target college's ApplicantPostSecondaryId
     */
    deletePostSecondary: async (applicantId: number, applicantPostSecondaryId: number): Promise<{ success: boolean; message?: string }> =>
        (await apiRequestUnwrapped<{ success: boolean; message?: string }>(
            `/api/documents/${applicantId}/post-secondary/${applicantPostSecondaryId}`,
            { method: "DELETE" }
        )) ?? { success: false },
    /**
     * Sets aside the applicant's signed high school transcript request after they changed their high school and
     * confirmed they will sign a new one. The signed version is kept server-side; the step reopens and the next
     * requestTranscriptAuthorization prepares a new document for the new school.
     * @param applicantId - The applicant ID
     */
    resetTranscriptAuthorization: async (applicantId: number): Promise<{ reset: number }> =>
        apiRequestUnwrapped<{ reset: number }>(`/api/documents/${applicantId}/transcript-authorization/reset`, {
            method: "POST",
            timeout: 60000,
        }),
    /**
     * Returns the secondary transcript-authorization signing status so the Secondary education form can
     * restore "signed"/waiting state on reload (the education load JSON doesn't carry it). Read-only:
     * does not generate or send anything to PandaDoc.
     * @param applicantId - The applicant ID
     */
    getTranscriptStatus: async (applicantId: number): Promise<{ applicantDocumentId: number | null; status: string | null; signed: boolean }> =>
        apiRequestUnwrapped<{ applicantDocumentId: number | null; status: string | null; signed: boolean }>(`/api/documents/${applicantId}/transcript-status`, {
            method: "GET",
            timeout: 60000,
        }),
    /**
     * Save the student's 1098-T opt-in consent
     * @param applicantId - The applicant ID
     * @param optIn1098T - The opt-in consent value (should be "Yes")
     * @returns Promise indicating success
     */
    saveOptIn1098T: async (applicantId: number, optIn1098T: string): Promise<{ success: boolean; message?: string }> =>
        apiRequestUnwrapped<{ success: boolean; message?: string }>(`/api/enrollment/applicant/${applicantId}/opt-in-1098t`, {
            method: "POST",
            data: { optIn1098T },
        }),
    /**
     * Save student references for an applicant
     * @param applicantId - The applicant ID
     * @param references - Array of student references to save
     * @returns Promise indicating success and validation status
     */
    saveStudentReferences: async (applicantId: number, references: any[]): Promise<{ success: boolean; hasCompleteReferences: boolean; message?: string }> =>
        apiRequestUnwrapped<{ success: boolean; hasCompleteReferences: boolean; message?: string }>(`/api/enrollment/applicant/${applicantId}/student-references`, {
            method: "POST",
            data: {
                applicantId,
                applicantEnrollmentId: 0, // Will be determined server-side
                references,
            },
        }),
    getPersonalProfileCompletionStatus: async (applicantId: number): Promise<IPersonalProfileCompletionStatus> =>
        apiRequestUnwrapped<IPersonalProfileCompletionStatus>(`/api/applicants/${applicantId}/personal-profile-status`, {
            method: "GET",
        }),
    // Marks a Program Enrollment panel complete for "continue from last panel". The
    // server only honors known enrollment panel GUIDs; callers treat failures as
    // non-fatal so the UI flow can proceed regardless.
    markStepComplete: async (panelId: string): Promise<void> => {
        await apiRequest<void>("/api/enrollment/complete-step", {
            method: "POST",
            data: { panelId },
        });
    },
    saveFerpaData: async (
        applicantId: number,
        ferpaData: {
            releaseInformationToSpecifiedParties?: boolean;
            doNotReleaseDirectoryInformation?: boolean;
            authorizedContacts?: Array<{
                firstName: string;
                lastName: string;
                relationship: string;
                informationToRelease?: string[];
            }>;
        }
    ): Promise<{ message: string; changed?: boolean }> =>
        apiRequestProblem<{ message: string; changed?: boolean }>(
            `/api/applicants/${applicantId}/ferpa`,
            {
                method: "POST",
                data: ferpaData,
            }
        ),
    /**
     * The applicant's saved FERPA election (release preference, directory flag and contacts) so the form can show what
     * they entered. Same shape saveFerpaData sends; the flags are null/undefined until a first save.
     */
    getFerpaData: async (applicantId: number): Promise<{
        releaseInformationToSpecifiedParties?: boolean | null;
        doNotReleaseDirectoryInformation?: boolean | null;
        authorizedContacts?: Array<{
            firstName: string;
            lastName: string;
            relationship: string;
            informationToRelease?: string[];
        }> | null;
    }> =>
        apiRequestUnwrapped(`/api/applicants/${applicantId}/ferpa`, {
            method: "GET",
            timeout: 60000,
        }),
    /**
     * Prepares the FERPA "Authorization to Release Information" for e-signature from the election saved by
     * saveFerpaData (save first) and returns the same DocFile shape as the transcript authorizations.
     * @param applicantId - The applicant ID
     */
    requestFerpaAuthorization: async (applicantId: number): Promise<DocFile | DocumentJobInfo> =>
        apiRequestUnwrapped<DocFile | DocumentJobInfo>(`/api/documents/${applicantId}/ferpa-authorization`, {
            method: "POST",
            timeout: 60000, // returns at once when queued; opening an existing document creates a signing session
        }),
    /**
     * The applicant reviewed their generated FERPA authorization (the form has their name applied, no signature).
     * Completes the FERPA step and panel on the server.
     * @param applicantId - The applicant ID
     */
    markFerpaReviewed: async (applicantId: number): Promise<{ applicantDocumentId: number; signed: boolean }> =>
        apiRequestUnwrapped<{ applicantDocumentId: number; signed: boolean }>(`/api/documents/${applicantId}/ferpa-reviewed`, {
            method: "POST",
            timeout: 60000,
        }),
    /**
     * Whether the applicant's current FERPA authorization is signed. The server reconciles with PandaDoc and, the
     * moment it is signed, completes the FERPA step and panel, so call this right after the viewer closes.
     * @param applicantId - The applicant ID
     */
    getFerpaStatus: async (applicantId: number): Promise<{ applicantDocumentId: number | null; status: string | null; signed: boolean }> =>
        apiRequestUnwrapped<{ applicantDocumentId: number | null; status: string | null; signed: boolean }>(`/api/documents/${applicantId}/ferpa-status`, {
            method: "GET",
            timeout: 60000,
        }),
};

// ---------- ProblemDetails / robust error ----------
export class ApiProblemError extends Error {
    status?: number;
    details?: unknown;
    fieldErrors?: Record<string, string[]>;
    constructor(message: string, status?: number, details?: unknown) {
        super(message);
        this.status = status;
        this.details = details;
        const d = details as Record<string, unknown> | undefined;
        const errs =
            d && typeof d === "object"
                ? (d as { errors?: Record<string, unknown> }).errors
                : undefined;
        if (errs && typeof errs === "object") {
            this.fieldErrors = Object.fromEntries(
                Object.entries(errs).map(([k, v]) => [
                    k,
                    Array.isArray(v) ? v.map(String) : [String(v)],
                ])
            );
        }
    }
}
export function buildApiUrl(endpoint: string): string {
    const base = getApiBaseUrl();
    return endpoint.startsWith("/api")
        ? base + endpoint
        : base +
              "/api" +
              (endpoint.startsWith("/") ? endpoint : "/" + endpoint);
}
// BEGIN: Added lightweight verb helpers used by enrollmentDraft.ts
export async function httpGet<T = unknown>(
    endpoint: string,
    params?: Record<string, unknown>,
    config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
    const url = buildApiUrl(endpoint);
    return apiClient.get<T>(url, {
        ...(config || {}),
        params,
        withCredentials: true,
        timeout: 30000,
    });
}
export async function httpPost<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
    const url = buildApiUrl(endpoint);
    return apiClient.post<T>(url, data, {
        ...(config || {}),
        withCredentials: true,
        timeout: 30000,
        headers: {
            "Content-Type": "application/json",
            ...(config?.headers || {}),
        },
    });
}
export async function httpPut<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
    const url = buildApiUrl(endpoint);
    return apiClient.put<T>(url, data, {
        ...(config || {}),
        withCredentials: true,
        timeout: 30000,
        headers: {
            "Content-Type": "application/json",
            ...(config?.headers || {}),
        },
    });
}
// END: Added verb helpers

// ---------- financial aid ----------
export interface IsirStatus {
    hasCompletedFAFSA: boolean;
}

/**
 * Checks whether the user has completed a FAFSA for the current or a future award year
 * by querying the ISIRcheck_VW view on the server. applicantId is required; studentId is
 * optional because some users do not yet have a student ID.
 */
export async function getIsirStatus(
    applicantId: number,
    studentId?: number | null
): Promise<IsirStatus> {
    const params = new URLSearchParams({ applicantId: String(applicantId) });
    if (studentId != null) {
        params.set("studentId", String(studentId));
    }
    return apiRequest<IsirStatus>(`/api/financial/isir-status?${params.toString()}`, {
        method: "GET",
    });
}

// Re-added: robust ProblemDetails request helper (was previously removed)
export async function apiRequestProblem<T>(
    endpoint: string,
    options: AxiosRequestConfig = {}
): Promise<T> {
    const fullUrl = buildApiUrl(endpoint);
    const config: AxiosRequestConfig = {
        url: fullUrl,
        withCredentials: true,
        timeout: 30000,
        headers: {
            "Content-Type": "application/json",
            ...(options.headers ?? {}),
        },
        ...options,
    };
    try {
        const response = await apiClient.request<T>(config);
        return response.data as T;
    } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            const data = error.response?.data;
            const title =
                (status === 403 && emailNotVerifiedMessage(data)) ||
                (data && (data.title || data.detail)) ||
                error.message ||
                "Request failed";
            throw new ApiProblemError(String(title), status, data);
        }
        throw new ApiProblemError("Unknown error");
    }
}

// ---------- Housing API ----------
export const housingApi = {
    fetch: async <T = unknown>(): Promise<T> =>
        apiRequestProblem<T>("/api/HousingForm/Fetch", { method: "GET" }),
    saveHousingDetails: async (
        details: IHousingDetailsData
    ): Promise<{ received: boolean; pushed: boolean; isEligible: boolean }> =>
        apiRequestProblem<{ received: boolean; pushed: boolean; isEligible: boolean }>("/api/HousingForm/SaveHousingDetails", {
            method: "POST",
            data: details,
        }),
    savePreviousAddress: async (
        prev: IPreviousAddressFormData,
        housingDetails?: IHousingDetailsData
    ): Promise<unknown> =>
        apiRequestProblem<unknown>("/api/HousingForm/SavePreviousAddress", {
            method: "POST",
            data: { previousAddress: prev, housingDetails },
        }),
    ensureHousingFee: async (): Promise<{
        received: boolean;
        created: boolean;
        applicantEnrollmentId: number;
        applicantEnrollmentFeeId: number;
    }> =>
        apiRequestProblem<{
            received: boolean;
            created: boolean;
            applicantEnrollmentId: number;
            applicantEnrollmentFeeId: number;
        }>("/api/HousingForm/EnsureHousingFee", {
            method: "POST",
        }),
    getHousingFee: async (): Promise<{ amount: number; feeStatusId: number }> =>
        apiRequestProblem<{ amount: number; feeStatusId: number }>("/api/HousingForm/GetHousingFee", {
            method: "GET",
        }),
    /**
     * Prepares the Housing Application for e-signature from the saved housing answers and previous address
     * (save those first) and returns the same DocFile shape as the transcript/FERPA authorizations.
     * @param applicantId - The applicant ID
     */
    requestHousingApplication: async (applicantId: number): Promise<DocFile | DocumentJobInfo> =>
        apiRequestUnwrapped<DocFile | DocumentJobInfo>(`/api/documents/${applicantId}/housing-application`, {
            method: "POST",
            timeout: 60000, // returns at once when queued; opening an existing document creates a signing session
        }),
    /**
     * Whether the applicant's current housing application is signed. The server reconciles with PandaDoc and, the
     * moment it is signed, completes the signature step and the Housing page, so call this right after the viewer closes.
     * @param applicantId - The applicant ID
     */
    getHousingApplicationStatus: async (applicantId: number): Promise<{ applicantDocumentId: number | null; status: string | null; signed: boolean }> =>
        apiRequestUnwrapped<{ applicantDocumentId: number | null; status: string | null; signed: boolean }>(`/api/documents/${applicantId}/housing-status`, {
            method: "GET",
            timeout: 60000,
        }),
};

// ---------- SensePass ----------
const SENSEPASS_COMMIT_FAILED =
    "Your payment could not be processed. Please check your card details and try again.";

export interface SensePassCommitResponse {
    isError?: boolean;
    message?: string;
    transactionNumber?: string;
    receipt?: PaymentReceipt | null;
}

export const sensePassApi = {
    /**
     * Commits a SensePass transaction on the backend for the given fees. Throws an Error carrying the
     * server's human-readable message (the server returns { isError, message }) rather than the raw
     * status/JSON, so callers can show `error.message` directly.
     */
    commit: async (
        transactionNumber: string,
        body: { applicantId?: number | null; applicantEnrollmentId?: number | null; selectedFeeIds: number[] }
    ): Promise<SensePassCommitResponse> => {
        let data: SensePassCommitResponse | null;
        try {
            data = await apiRequest<SensePassCommitResponse | null>(
                `/api/sensepass/commit/${encodeURIComponent(transactionNumber)}`,
                { method: "POST", data: body, timeout: 120000 } // the vendor call can be slow
            );
        } catch (error: unknown) {
            const serverMessage = axios.isAxiosError(error)
                ? (error.response?.data as { message?: string } | undefined)?.message
                : undefined;
            throw new Error(serverMessage || SENSEPASS_COMMIT_FAILED);
        }

        // apiRequest turns a 401 into null; that is a failed commit, never a successful one.
        if (data == null) {
            throw new Error("Your session has expired. Please sign in again before paying.");
        }
        if (data.isError) {
            throw new Error(data.message || "Failed to commit transaction");
        }
        return data;
    },
};

// ---------- Interviews ----------
export interface FinancialBookingsStatus {
    bookingsUrl?: string | null;
    campusPhone?: string | null;
    featureEnabled: boolean;
}

export const interviewsApi = {
    getProfileCompleteStatus: async (): Promise<InterviewStatus> =>
        apiRequestUnwrapped<InterviewStatus>(
            "/api/interviews/profile-complete-status",
            { method: "GET" }
        ) ?? ({} as InterviewStatus),
    getFinancialBookingsStatus: async (): Promise<FinancialBookingsStatus> =>
        apiRequestUnwrapped<FinancialBookingsStatus>(
            "/api/interviews/financial-bookings-status",
            { method: "GET" }
        ) ?? ({ featureEnabled: false } as FinancialBookingsStatus),
    getAdmissionsInterviewStatus: async (): Promise<AdmissionsInterviewStatus> =>
        apiRequestUnwrapped<AdmissionsInterviewStatus>(
            "/api/interviews/admissions-interview-status",
            { method: "GET" }
        ) ?? ({ status: "Schedule" } as AdmissionsInterviewStatus),
    // Marks an Admissions Interview panel (intro/booking) complete for "continue
    // from last panel". The server only honors known interview panel GUIDs; callers
    // treat failures as non-fatal so the UI flow can proceed regardless.
    markStepComplete: async (panelId: string): Promise<void> => {
        await apiRequest<void>("/api/interviews/complete-step", {
            method: "POST",
            data: { panelId },
        });
    },
};

// ---------- Documents ----------


// ---------- Debug API ----------
export const debugApi = {
    testConnection: async (): Promise<Record<string, unknown>> =>
        apiRequest<Record<string, unknown>>("/api/auth/test", {
            method: "GET",
        }),
    testCheckAccount: async (
        identifier: string
    ): Promise<CheckAccountResponse> => authApi.checkAccount(identifier),
    testSignalRHub: async (): Promise<Record<string, unknown>> => {
        const hubUrl = absoluteUrl("hubs/emailNotification");
        try {
            const response = await apiClient.post(
                hubUrl + "/negotiate",
                {},
                {
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                    withCredentials: true,
                }
            );
            return { success: true, data: response.data };
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const status = error.response?.status;
                const errPayload = error.response?.data;
                logError("SignalR negotiate failed:", errPayload ?? status);
                return {
                    success: false,
                    error: errPayload ?? error.message,
                    status,
                };
            }
            return {
                success: false,
                error: (error as Error)?.message ?? "Unknown error",
            };
        }
    },
    testAllEndpoints: async (): Promise<{
        api: unknown;
        signalr: unknown;
        environment: Record<string, unknown>;
    }> => {
        const results: {
            api: unknown;
            signalr: unknown;
            environment: Record<string, unknown>;
        } = {
            api: null,
            signalr: null,
            environment: {
                MODE: import.meta.env.MODE,
                DEV: import.meta.env.DEV,
                BASE_URL: BASE_PATH,
                VITE_API_TARGET: import.meta.env.VITE_API_TARGET,
                VITE_HUB_TARGET: import.meta.env.VITE_HUB_TARGET,
                VITE_HUBS_TARGET: import.meta.env.VITE_HUBS_TARGET,
                location: window.location.href,
                detectedApiBase: getApiBaseUrl(),
            },
        };
        try {
            results.api = await debugApi.testConnection();
        } catch (e) {
            results.api = { error: e };
        }
        try {
            results.signalr = await debugApi.testSignalRHub();
        } catch (e) {
            results.signalr = { error: e };
        }
        return results;
    },
    logRequest: (_endpoint: string, _config: AxiosRequestConfig) => {
        return;
    },
    logResponse: (_endpoint: string, _response: AxiosResponse) => {
        return;
    },
    logError: (endpoint: string, error: AxiosError) => {
        logError(`API Error: ${endpoint}`, error);
    },
};

// Make debug utilities globally accessible for dev
if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).debugApi = debugApi;
    (window as unknown as Record<string, unknown>).authApi = authApi;
    (window as unknown as Record<string, unknown>).getApiBaseUrl =
        getApiBaseUrl;
}

// prefix unused var
const _MeDataSchema = z.union([
    User.schema,
    RegisterResponse.schema,
    LoginResponse.schema,
]);
